<template>
    <template>
        <!-- Geofencings complets -->
        <template v-for="geofencing in normalizedGeofencings" :key="'gf-' + geofencing.id">
            <l-polygon
                v-if="geofencing.polygon.length >= 3"
                :lat-lngs="geofencing.polygon"
                :fill="true"
                :fillOpacity="fillOpacity"
                :weight="weight"
                :opacity="opacity"
                :color="color"
            />

            <template v-if="showPoints">
                <l-circle-marker
                    v-for="(pt, i) in geofencing.polygon"
                    :key="'gfpt-' + geofencing.id + '-' + i"
                    :lat-lng="pt"
                    :radius="pointRadius"
                    :weight="2"
                    :fill="true"
                    :fillColor="color"
                    :fillOpacity="0.9"
                    :opacity="1"
                    :color="color"
                >
                    <l-tooltip
                        v-if="showLabels"
                        :content="getPointLabel(geofencing, i)"
                        :permanent="labelsPermanent"
                        direction="top"
                    />
                </l-circle-marker>
            </template>
        </template>

        <!-- Polygone direct déjà normalisé [[lat, lng], ...] -->
        <template v-if="!isDrawing && normalizedPolygon.length">
            <l-polygon
                v-if="normalizedPolygon.length >= 3"
                :lat-lngs="normalizedPolygon"
                :fill="true"
                :fillOpacity="fillOpacity"
                :weight="weight"
                :opacity="opacity"
                :color="color"
            />

            <template v-if="showPoints">
                <l-circle-marker
                    v-for="(pt, i) in normalizedPolygon"
                    :key="'polypt-' + i"
                    :lat-lng="pt"
                    :radius="pointRadius"
                    :weight="2"
                    :fill="true"
                    :fillColor="color"
                    :fillOpacity="0.9"
                    :opacity="1"
                    :color="color"
                >
                    <l-tooltip
                        v-if="showLabels"
                        :content="String(i + 1)"
                        :permanent="labelsPermanent"
                        direction="top"
                    />
                </l-circle-marker>
            </template>
        </template>

        <!-- Mode dessin -->
        <template v-if="isDrawing">
            <l-polyline
                v-if="normalizedDraftPoints.length >= 1"
                :lat-lngs="normalizedDraftPoints"
                :weight="3"
                :color="draftColor"
            />

            <l-polygon
                v-if="normalizedDraftPoints.length >= 3"
                :lat-lngs="normalizedDraftPoints"
                :fill="true"
                :fillOpacity="0.15"
                :weight="3"
                :color="draftColor"
            />

            <l-circle-marker
                v-for="(pt, i) in normalizedDraftPoints"
                :key="'draftpt-' + i"
                :lat-lng="pt"
                :radius="pointRadius"
                :weight="2"
                :fill="true"
                :fillColor="draftColor"
                :fillOpacity="0.9"
                :opacity="1"
                :color="draftColor"
            >
                <l-tooltip
                    v-if="showLabels"
                    :content="String(i + 1)"
                    :permanent="labelsPermanent"
                    direction="top"
                />
            </l-circle-marker>

            <l-polyline
                v-if="normalizedDraftPoints.length >= 2"
                :lat-lngs="[normalizedDraftPoints[normalizedDraftPoints.length - 1], normalizedDraftPoints[0]]"
                :weight="2"
                :opacity="0.7"
                :dashArray="[6, 6]"
                :color="draftColor"
            />
        </template>
    </template>
</template>

<script>
import { LPolygon, LPolyline, LCircleMarker, LTooltip } from "@vue-leaflet/vue-leaflet";

export default {
    name: "GeofencingLayer",
    components: {
        LPolygon,
        LPolyline,
        LCircleMarker,
        LTooltip,
    },
    props: {
        geofencings: {
            type: Array,
            default: () => [],
        },
        polygon: {
            type: Array,
            default: () => [],
        },
        draftPoints: {
            type: Array,
            default: () => [],
        },
        isDrawing: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String,
            default: "#0d6efd",
        },
        draftColor: {
            type: String,
            default: "#198754",
        },
        fillOpacity: {
            type: Number,
            default: 0.2,
        },
        opacity: {
            type: Number,
            default: 1,
        },
        weight: {
            type: Number,
            default: 4,
        },
        pointRadius: {
            type: Number,
            default: 6,
        },
        showPoints: {
            type: Boolean,
            default: true,
        },
        showLabels: {
            type: Boolean,
            default: true,
        },
        labelsPermanent: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        normalizedPolygon() {
            return this.normalizeLatLngArray(this.polygon);
        },
        normalizedDraftPoints() {
            return this.normalizeLatLngArray(this.draftPoints);
        },
        normalizedGeofencings() {
            return this.geofencings
                .map((geofencing) => ({
                    id: geofencing.id,
                    name: geofencing.name,
                    polygon: this.buildPolygon(geofencing),
                }))
                .filter((geofencing) => geofencing.polygon.length >= 3);
        },
    },
    methods: {
        normalizeLatLngArray(points) {
            if (!Array.isArray(points)) {
                return [];
            }

            return points
                .map((pt) => {
                    if (Array.isArray(pt)) {
                        return [Number(pt[0]), Number(pt[1])];
                    }

                    return [
                        Number(pt.latitude ?? pt.lat),
                        Number(pt.longitude ?? pt.lng),
                    ];
                })
                .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
        },
        buildPolygon(geofencing) {
            const points = Array.isArray(geofencing?.geofencingPoints)
                ? geofencing.geofencingPoints
                : [];

            return points
                .slice()
                .sort((a, b) => Number(a.orderIndex ?? 0) - Number(b.orderIndex ?? 0))
                .map((point) => [
                    Number(point.latitude),
                    Number(point.longitude),
                ])
                .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
        },
        getPointLabel(geofencing, index) {
            if (geofencing.name) {
                return `${geofencing.name} #${index + 1}`;
            }

            return String(index + 1);
        },
    },
};
</script>